import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  RiUserLine,
  RiAddLine,
  RiFilter2Line,
  RiSearchLine,
  RiPencilLine,
  RiDeleteBin6Line,
  RiEyeLine,
  RiEyeOffLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiAlertLine,
  RiRefreshLine,
  RiMapPinLine,
  RiPhoneLine,
  RiMailLine,
  RiMoneyDollarCircleLine,
  RiBuildingLine,
  RiLayoutGridLine,
  RiListUnordered,
  RiUserHeartLine,
  RiWallet3Line,
  RiCalendarLine,
  RiFileTextLine,
  RiHomeSmileLine,
  RiToolsLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiUserStarLine,
  RiTimeLine,
  RiUser3Line,
  RiShieldCheckLine,
  RiPercentLine,
  RiBankLine,
  RiHandCoinLine,
  RiHomeLine,
  RiPageSeparator,
  RiInformationLine,
  RiHome4Fill,
  RiRulerLine,
  RiHomeGearLine,
  RiFileUserLine,
  RiDashboardLine,
  RiFilterLine,
  RiSettings4Line,
  RiUser6Line,
  RiCalendarCheckLine
} from 'react-icons/ri';
import {
  getAllTenants,
  deleteTenant,
  updateTenantStatus,
  getRentStatusColor,
  formatTenantDisplayName,
  formatCurrency,
  formatDate,
  TENANT_RENT_STATUS_OPTIONS,
  calculateRentAfterDiscount,
  isLeaseExpired
} from '../../../models/tenantModel';
import { getOwner } from '../../../models/ownerModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { getCurrentUser, isAuthenticated } from '../../../models/authModel';

const TenantList = ({ filter }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const getStatusLineColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]';
      case 'partial': return 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.3)]';
      case 'overdue': return 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.3)]';
      case 'expired': return 'bg-zinc-500 shadow-[0_0_10px_rgba(113,113,122,0.3)]';
      case 'pending': return 'bg-zinc-400 shadow-[0_0_10px_rgba(161,161,170,0.3)]';
      default: return 'bg-zinc-400';
    }
  };

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedTenants, setExpandedTenants] = useState({});
  const [ownerDetails, setOwnerDetails] = useState({});
  const [loadingOwners, setLoadingOwners] = useState({});
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [filters, setFilters] = useState({
    rentStatus: filter === 'overdue' ? 'overdue' : '',
    buildingId: '',
    apartmentId: '',
    searchTerm: '',
    showInactive: true,
    minRent: '',
    maxRent: '',
    contractEnd: ''
  });

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    tenantId: null,
    tenantName: ''
  });

  const [deleting, setDeleting] = useState(false);

  // Debug authentication
  useEffect(() => {
    const currentUser = getCurrentUser();
    const isAuth = isAuthenticated();
    const token = localStorage.getItem('token');

    console.log('Authentication Debug:', {
      isAuthenticated: isAuth,
      hasToken: !!token,
      currentUser: currentUser,
      userRole: currentUser?.role
    });

    if (!isAuth) {
      setError(t('not_authenticated'));
      setLoading(false);
      return;
    }
  }, [t]);

  // Handle filter prop changes
  useEffect(() => {
    if (filter === 'overdue') {
      setFilters(prev => ({ ...prev, rentStatus: 'overdue' }));
    } else if (!filter) {
      setFilters(prev => ({ ...prev, rentStatus: '' }));
    }
  }, [filter]);

  // Fetch tenants data
  const fetchTenants = async (page = currentPage) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Fetching tenants with filters:', filters);

      const apiFilters = {
        page,
        limit: itemsPerPage
      };

      // Map frontend filter names to backend filter names
      if (filters.rentStatus) apiFilters.rentStatus = filters.rentStatus;
      if (filters.buildingId) apiFilters.buildingId = filters.buildingId;
      if (filters.apartmentId) apiFilters.apartmentId = filters.apartmentId;
      if (filters.minRent) apiFilters.minRent = parseFloat(filters.minRent);
      if (filters.maxRent) apiFilters.maxRent = parseFloat(filters.maxRent);
      if (filters.contractEnd) apiFilters.contractEndBefore = filters.contractEnd;

      // Add sort parameters
      apiFilters.sortBy = sortField;
      apiFilters.sortDirection = sortDirection;

      console.log('📋 API Filters:', apiFilters);

      // Check authentication before making request
      const currentUser = getCurrentUser();
      const token = localStorage.getItem('token');

      if (!token || !currentUser) {
        throw new Error(t('authentication_required'));
      }

      console.log('🔑 Making authenticated request...');

      const response = await getAllTenants(apiFilters);

      console.log('✅ API Response:', response);

      // FIXED: Handle the correct response structure
      if (response && response.success) {
        let tenantsData = [];
        let totalItems = 0;

        // Check if response.tenants is an object with nested tenants array (new structure)
        if (response.tenants && typeof response.tenants === 'object' && response.tenants.tenants) {
          tenantsData = response.tenants.tenants;
          totalItems = response.tenants.total || tenantsData.length;
          console.log('📦 Using nested structure:', {
            tenantsArray: tenantsData,
            total: totalItems,
            page: response.tenants.page,
            limit: response.tenants.limit
          });
        }
        // Fallback: Check if response.tenants is directly an array (old structure)
        else if (response.tenants && Array.isArray(response.tenants)) {
          tenantsData = response.tenants;
          totalItems = response.total || tenantsData.length;
          console.log('📦 Using direct array structure:', {
            tenantsArray: tenantsData,
            total: totalItems
          });
        }
        // Handle case where no tenants are found
        else {
          console.warn('⚠️ No tenants found in response structure:', response);
          tenantsData = [];
          totalItems = 0;
        }

        if (Array.isArray(tenantsData)) {
          // Process tenants to match frontend expected format
          const processedTenants = tenantsData.map(tenant => {
            // Split name into first_name and last_name if needed
            let first_name = '', last_name = '';
            if (tenant.name) {
              const nameParts = tenant.name.split(' ');
              first_name = nameParts[0] || '';
              last_name = nameParts.slice(1).join(' ') || '';
            }

            // Calculate rentAfterDiscount if it doesn't exist
            const rentAfterDiscount = tenant.rentAfterDiscount || calculateRentAfterDiscount(tenant.rent, tenant.discount);

            // Use actual backend status
            const isActive = tenant.status === 'active';

            return {
              ...tenant,
              first_name: first_name,
              last_name: last_name,
              rentAfterDiscount,
              is_active: isActive,
              active_leases: tenant.apartmentId ? 1 : 0,
              total_leases: 1,
              // Owner information (already included from backend)
              ownerId: tenant.ownerId,
              ownerName: tenant.ownerName,
              ownerPhone: tenant.ownerPhone,
              ownerLivingThere: tenant.ownerLivingThere
            };
          });

          setTenants(processedTenants);
          setTotalItems(totalItems);
          setDebugInfo(`Loaded ${processedTenants.length} tenants successfully`);
          console.log('👥 Tenants loaded:', processedTenants);

          // Reset selected tenants when fetching new data
          setSelectedTenants([]);
          setSelectAll(false);
        } else {
          console.error('❌ Tenants data is not an array:', tenantsData);
          setTenants([]);
          setTotalItems(0);
          setDebugInfo('Tenants data is not an array');
        }
      } else {
        console.warn('⚠️ API response missing success or incorrect structure:', response);
        setTenants([]);
        setTotalItems(0);
        setDebugInfo('API response missing success flag or incorrect structure');
      }

    } catch (err) {
      console.error('❌ Error fetching tenants:', err);
      console.error('📋 Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        stack: err.stack
      });

      let errorMessage = t('failed_to_load_tenants_data');

      if (err.response?.status === 401) {
        errorMessage = t('authentication_failed');
      } else if (err.response?.status === 403) {
        errorMessage = t('access_denied');
      } else if (err.response?.status === 500) {
        errorMessage = t('server_error');
      } else if (err.message.includes('Network Error') || err.code === 'ERR_NETWORK') {
        errorMessage = t('cannot_connect_to_server');
      }

      setError(errorMessage);
      setDebugInfo(err.message || t('unknown_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants(1); // Reset to first page when filters or sort options change
    setCurrentPage(1);
  }, [filters.rentStatus, filters.apartmentId, filters.buildingId,
  filters.minRent, filters.maxRent, filters.contractEnd,
    sortField, sortDirection, itemsPerPage]);

  // Fetch owner details for a tenant's apartment
  const fetchOwnerDetails = async (ownerId, tenantId) => {
    if (!ownerId || ownerDetails[ownerId]) return; // Skip if no owner ID or already loaded

    try {
      setLoadingOwners(prev => ({ ...prev, [tenantId]: true }));

      const response = await getOwner(ownerId);

      if (response && response.owner) {
        setOwnerDetails(prev => ({
          ...prev,
          [ownerId]: response.owner
        }));
      }
    } catch (err) {
      console.error(`Error fetching owner ${ownerId} details:`, err);
    } finally {
      setLoadingOwners(prev => ({ ...prev, [tenantId]: false }));
    }
  };

  // Toggle tenant expansion to show more details
  const toggleTenantExpansion = (tenantId, ownerId) => {
    setExpandedTenants(prev => {
      const isCurrentlyExpanded = prev[tenantId];

      // If expanding and there's an owner ID, fetch owner details
      if (!isCurrentlyExpanded && ownerId) {
        fetchOwnerDetails(ownerId, tenantId);
      }

      return {
        ...prev,
        [tenantId]: !isCurrentlyExpanded
      };
    });
  };

  // Manual refresh function
  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    fetchTenants(currentPage);
  };

  // Handle search
  const handleSearch = (e) => {
    setFilters({ ...filters, searchTerm: e.target.value });
  };

  // Apply search filter
  const handleApplySearch = () => {
    fetchTenants(1); // Reset to first page when searching
    setCurrentPage(1);
  };

  // Toggle show inactive tenants
  const toggleShowInactive = () => {
    setFilters({ ...filters, showInactive: !filters.showInactive });
  };

  // Toggle view mode
  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  };

  // Handle sorting
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTenants(page);
  };

  // Filter tenants based on search term and active status
  const filteredTenants = tenants.filter(tenant => {
    const searchTerm = filters.searchTerm.toLowerCase();

    // Search filter logic
    let matchesSearch = true;

    if (searchTerm.trim()) {
      // Handle different name formats (name or first_name/last_name)
      const fullName = tenant.name ? tenant.name.toLowerCase() :
        `${tenant.first_name || ''} ${tenant.last_name || ''}`.toLowerCase();

      matchesSearch = (
        fullName.includes(searchTerm) ||
        (tenant.email && tenant.email.toLowerCase().includes(searchTerm)) ||
        (tenant.phone && tenant.phone.toLowerCase().includes(searchTerm)) ||
        (tenant.apartmentNumber && tenant.apartmentNumber.toString().toLowerCase().includes(searchTerm)) ||
        (tenant.buildingName && tenant.buildingName.toLowerCase().includes(searchTerm))
      );
    }

    // If showInactive is false, hide expired tenants (DB status or past contract date)
    if (!filters.showInactive && isLeaseExpired(tenant)) {
      return false;
    }

    return matchesSearch;
  });

  // Handle check/uncheck all
  const handleSelectAll = () => {
    if (selectAll) {
      // Unselect all
      setSelectedTenants([]);
    } else {
      // Select all
      setSelectedTenants(tenants.map(tenant => tenant.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual selection
  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev => {
      if (prev.includes(tenantId)) {
        // Remove from selection
        const newSelection = prev.filter(id => id !== tenantId);
        setSelectAll(newSelection.length === tenants.length);
        return newSelection;
      } else {
        // Add to selection
        const newSelection = [...prev, tenantId];
        setSelectAll(newSelection.length === tenants.length);
        return newSelection;
      }
    });
  };

  // Handle delete confirmation dialog
  const openDeleteDialog = (tenantId, tenantName) => {
    setDeleteDialog({
      isOpen: true,
      tenantId,
      tenantName
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      isOpen: false,
      tenantId: null,
      tenantName: ''
    });
  };

  // Handle tenant deletion
  const handleDeleteTenant = async () => {
    if (!deleteDialog.tenantId) return;

    try {
      setDeleting(true);

      await deleteTenant(deleteDialog.tenantId);

      // Refresh tenants list
      fetchTenants(currentPage);

      // Close dialog
      closeDeleteDialog();

    } catch (err) {
      console.error('Error deleting tenant:', err);
      setError(err.message || t('failed_to_delete_tenant'));
    } finally {
      setDeleting(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedTenants.length === 0) return;

    const confirmDelete = window.confirm(
      t('confirm_bulk_delete', { count: selectedTenants.length })
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);

      // In a real implementation, you would have a bulk delete API endpoint
      // For now, we'll delete them one by one
      for (const tenantId of selectedTenants) {
        await deleteTenant(tenantId);
      }

      // Refresh the tenant list
      fetchTenants(currentPage);

      // Clear selection
      setSelectedTenants([]);
      setSelectAll(false);

    } catch (err) {
      console.error('Error with bulk delete:', err);
      setError(err.message || t('failed_to_delete_tenants'));
    } finally {
      setLoading(false);
    }
  };

  // Toggle tenant status
  const toggleTenantStatus = async (tenantId, currentStatus) => {
    try {
      // In the backend, we update the rent status (paid, pending, overdue)
      // Map frontend active/inactive to backend rentStatus
      const newRentStatus = currentStatus ? 'pending' : 'paid';

      await updateTenantStatus(tenantId, newRentStatus);

      // Update the tenant's status in the local state
      setTenants(tenants.map(tenant =>
        tenant.id === tenantId
          ? { ...tenant, rentStatus: newRentStatus, is_active: !currentStatus }
          : tenant
      ));

    } catch (err) {
      console.error('Error toggling tenant status:', err);
      setError(err.message || t('failed_to_update_tenant_status'));
    }
  };


  // Count tenants by status using actual backend status
  const activeTenantsCount = tenants.filter(t => !isLeaseExpired(t)).length;
  const inactiveTenantsCount = tenants.filter(t => isLeaseExpired(t)).length;
  const tenantsWithActiveLeasesCount = tenants.filter(t => t.apartmentId).length;
  const tenantsWithoutActiveLeasesCount = tenants.filter(t => !t.apartmentId).length;

  // Calculate pagination
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Direction-aware utilities for RTL support
  const getMarginClass = (direction, size) => {
    if (direction === "start") {
      return `ms-${size}`;
    } else if (direction === "end") {
      return `me-${size}`;
    }
    return "";
  };

  const getPaddingClass = (direction, size) => {
    if (direction === "start") {
      return `ps-${size}`;
    } else if (direction === "end") {
      return `pe-${size}`;
    }
    return "";
  };

  const getFlexDirection = () => {
    return "flex-row";
  };

  const getRoundedClass = (direction, size) => {
    if (direction === "start") {
      return `rounded-s-${size}`;
    } else if (direction === "end") {
      return `rounded-e-${size}`;
    }
    return "";
  };

  // Sort tenants by ID descending (newest on top) for the default view
  const sortedTenants = [...filteredTenants].sort((a, b) => (b.id || 0) - (a.id || 0));

  // Grid View Component with improved RTL support and enhanced visuals
  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-start">
      {sortedTenants.map((tenant) => {
        const isExpanded = expandedTenants[tenant.id] || false;
        const owner = tenant.ownerId ? ownerDetails[tenant.ownerId] : null;
        const isLoading = loadingOwners[tenant.id] || false;

        return (
          <div
            key={tenant.id}
            className={`relative overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} border rounded-xl shadow-sm hover:shadow-md transition-all duration-300`}
          >
            {/* Status indicator line at top */}
            <div className={`absolute top-0 start-0 end-0 h-1.5 ${getStatusLineColor(tenant.rentStatus)}`}></div>

            <div className="relative pt-6"> {/* Added padding-top to account for status line */}
              {/* Selection Checkbox */}
              <div className={`absolute top-4 ${isRTL ? 'end-4' : 'start-4'} z-10`}>
                <input
                  type="checkbox"
                  checked={selectedTenants.includes(tenant.id)}
                  onChange={() => handleSelectTenant(tenant.id)}
                  className={`h-5 w-5 rounded ${isDarkMode ? 'bg-zinc-800 border-gray-600' : 'bg-white border-gray-300'
                    } text-red-600 focus:ring-red-500`}
                />
              </div>

              {/* Tenant Header */}
              <div className={`px-4 py-3 ${getPaddingClass("start", 12)} ${isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center">
                    <div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {tenant.name || formatTenantDisplayName(tenant)}
                      </h3>
                      <div className="flex flex-col gap-1 mt-1">
                        <div className="flex items-center text-sm text-gray-500">
                          <RiBuildingLine className={getMarginClass("end", 1)} size={14} />
                          <span>
                            {tenant.apartmentNumber ?
                              `${t('apt')} ${tenant.apartmentNumber}${tenant.floor ? ` · ${t('floor')} ${tenant.floor}` : ''} - ${tenant.buildingName || t('unknown_building')}` :
                              t('no_apartment_assigned')}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <RiPhoneLine className={getMarginClass("end", 1)} size={14} />
                          <span>{tenant.phone || t('no_phone')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lease status + rent status badges */}
                  <div className="flex flex-col gap-1 items-end">
                    {isLeaseExpired(tenant) && tenant.rentStatus !== 'expired' && (
                      <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                        {t('lease_ended')}
                      </span>
                    )}
                    <span className={`inline-flex px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-full ${getRentStatusColor(tenant.rentStatus, isDarkMode)} shadow-sm`}>
                      {tenant.rentStatus ? t(tenant.rentStatus.toLowerCase()) : t('status_unknown')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tenant Details */}
              <div className="p-4">

                {/* Financial Summary */}
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <h4 className={`text-xs uppercase tracking-wider font-semibold mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('financial_summary')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <div className="flex items-center">
                        <RiMoneyDollarCircleLine className={isDarkMode ? 'text-emerald-400' : 'text-emerald-500'} size={20} />
                        <span className={`${getMarginClass("start", 2)} text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                          {t('monthly_rent')}:
                        </span>
                      </div>
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                        {formatCurrency(tenant.rent || 0)}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center">
                        <RiPercentLine className={isDarkMode ? 'text-red-400' : 'text-red-500'} size={20} />
                        <span className={`${getMarginClass("start", 2)} text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                          {tenant.discount > 0 ? t('after_discount') : t('payment_date')}
                        </span>
                      </div>
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                        {tenant.discount > 0 ?
                          formatCurrency(tenant.rentAfterDiscount) :
                          formatDate(tenant.paymentDate) || t('not_set')}
                      </p>
                    </div>
                  </div>

                  <div className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} pt-3`}>
                    <div className="flex justify-between items-center">
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t('balance')}:
                      </span>
                      <span className={`text-lg font-bold ${parseFloat(tenant.balance) > 0 ? (isDarkMode ? 'text-rose-400' : 'text-rose-600') : (isDarkMode ? 'text-emerald-400' : 'text-emerald-600')}`}>
                        {formatCurrency(tenant.balance || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contract Period */}
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <h4 className={`text-xs uppercase tracking-wider font-semibold mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('contract_period')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center">
                        <RiCalendarLine className={isDarkMode ? 'text-red-400' : 'text-red-500'} size={18} />
                        <span className={`${getMarginClass("start", 2)} text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                          {t('start_date')}:
                        </span>
                      </div>
                      <p className={`text-base font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                        {formatDate(tenant.contractStartDate) || t('not_set')}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center">
                        <RiCalendarCheckLine className={isDarkMode ? 'text-red-400' : 'text-red-500'} size={18} />
                        <span className={`${getMarginClass("start", 2)} text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                          {t('end_date')}:
                        </span>
                      </div>
                      <p className={`text-base font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                        {tenant.contractEndDate ? `${formatDate(tenant.contractEndDate)} ${t('at_12_pm')}` : t('not_set')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Fees */}
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  <h4 className={`text-xs uppercase tracking-wider font-semibold mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'} flex items-center`}>
                    <RiFileTextLine className={getMarginClass("end", 1)} size={14} />
                    {t('additional_fees')}
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className={`p-2 rounded ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} text-center`}>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('service')}</p>
                      <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                        {formatCurrency(tenant.serviceFee || 0)}
                      </p>
                    </div>
                    <div className={`p-2 rounded ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} text-center`}>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('commission')}</p>
                      <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                        {formatCurrency(tenant.officeCommission || 0)}
                      </p>
                    </div>
                    <div className={`p-2 rounded ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} text-center`}>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('insurance')}</p>
                      <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                        {formatCurrency(tenant.insurance || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Show more details button */}
                <button
                  onClick={() => toggleTenantExpansion(tenant.id, tenant.ownerId)}
                  className={`w-full flex items-center justify-center py-2 px-4 text-sm rounded-lg transition-all duration-200 ${isDarkMode
                    ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  {isExpanded ? (
                    <>
                      <RiArrowUpSLine className={getMarginClass("end", 1)} />
                      {t('hide_owner_details')}
                    </>
                  ) : (
                    <>
                      <RiArrowDownSLine className={getMarginClass("end", 1)} />
                      {t('show_owner_details')}
                    </>
                  )}
                </button>
              </div>

              {/* Expanded Owner Details Section */}
              {isExpanded && (
                <div className={`p-4 ${isDarkMode ? 'bg-gray-750 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-200'}`}>
                  <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center`}>
                    <RiUser3Line className={getMarginClass("end", 2)} /> {t('apartment_owner')}
                  </h4>

                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className={`inline-block h-5 w-5 animate-spin rounded-full border-2 ${isDarkMode ? 'border-red-400 border-t-transparent' : 'border-red-500 border-t-transparent'
                        }`}></div>
                      <span className={`${getMarginClass("start", 2)} text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {t('loading_owner_details')}
                      </span>
                    </div>
                  ) : !owner ? (
                    <div className={`text-center py-3 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                      {t('no_owner_information')}
                    </div>
                  ) : (
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <div className="flex items-start">
                        <div className={`h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md ${getMarginClass("end", 3)} flex-shrink-0`}>
                          <span className="font-bold text-white text-sm">
                            {owner.name?.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h5 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{owner.name}</h5>
                          <div className="flex items-center text-sm mt-1">
                            <RiPhoneLine className={`${getMarginClass("end", 2)} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={14} />
                            <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{owner.phone}</span>
                          </div>

                          <div className="grid grid-cols-2 gap-2 mt-3">
                            <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                              <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('lives_on_property')}</p>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {owner.livingThere ? t('yes') : t('no')}
                              </p>
                            </div>
                            <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                              <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('total_apartments')}</p>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {owner.total_apartments || '1'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-dashed border-gray-500 border-opacity-30">
                        <h6 className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'} flex items-center`}>
                          <RiHomeGearLine className={getMarginClass("end", 1)} size={14} />
                          {t('apartment_details')}
                        </h6>
                        <div className="grid grid-cols-2 gap-2">
                          <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('apartment_number')}</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {tenant.apartmentNumber || t('unknown')}
                            </p>
                          </div>
                          <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('floor')}</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {tenant.floor || t('unknown')}
                            </p>
                          </div>
                          <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('apartment_type')}</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {tenant.type || t('standard')}
                            </p>
                          </div>
                          <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('furnished')}</p>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {tenant.isFurnished ? t('yes') : t('no')}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 text-end">
                        <Link
                          to={`/admin/owners/${owner.id}`}
                          className={`inline-flex items-center px-3 py-1 text-xs rounded-full ${isDarkMode ? 'bg-red-900 bg-opacity-40 text-red-400 hover:bg-opacity-60' : 'bg-zinc-50 text-red-600 hover:bg-zinc-100'}`}
                        >
                          <RiUser6Line className={getMarginClass("end", 1)} size={12} />
                          {t('view_owner_profile')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tenant Actions */}
              <div className={`px-4 py-3 ${isDarkMode ? 'bg-zinc-900 border-t border-gray-700' : 'bg-gray-100 border-t border-gray-200'}`}>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Link
                    to={`/admin/tenants/${tenant.id}`}
                    className={`flex-1 flex items-center justify-center py-2 px-4 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-red-900 bg-opacity-40 text-red-400 hover:bg-opacity-60' : 'bg-red-50 text-red-600 hover:bg-red-100'
                      }`}
                  >
                    <RiEyeLine className={getMarginClass("end", 2)} size={18} />
                    {t('view_details')}
                  </Link>

                  <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} gap-2 ${getMarginClass("start", 2)}`}>
                    <Link
                      to={`/admin/tenants/${tenant.id}/edit`}
                      className={`p-2 flex-shrink-0 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      title={t('edit_tenant')}
                    >
                      <RiPencilLine size={18} />
                    </Link>

                    {(getCurrentUser()?.role === 'admin' || getCurrentUser()?.role === 'front_desk' || getCurrentUser()?.role === 'sales') && (
                      <button
                        onClick={() => openDeleteDialog(tenant.id, tenant.name || formatTenantDisplayName(tenant))}
                        className={`p-2 flex-shrink-0 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-red-400 hover:text-red-300 hover:bg-gray-600' : 'bg-gray-200 text-red-600 hover:bg-red-100'
                          }`}
                        title={t('delete_tenant')}
                      >
                        <RiDeleteBin6Line size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // List View Component with improved RTL support and enhanced visuals
  const ListView = () => {

    return (
      <div className="overflow-x-auto rounded-lg border shadow-sm">
        <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700 border-gray-700' : 'divide-gray-200 border-gray-200'} border-collapse`}>
          <thead className={isDarkMode ? 'bg-zinc-900' : 'bg-gray-50'}>
            <tr>
              <th scope="col" className="px-3 py-3 text-start">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className={`h-4 w-4 rounded ${isDarkMode ? 'bg-zinc-800 border-gray-600' : 'bg-white border-gray-300'
                    } text-red-600 focus:ring-red-500`}
                />
              </th>
              <th
                scope="col"
                className={`px-3 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                  } uppercase tracking-wider cursor-pointer`}
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  {t('tenant')}
                  {sortField === 'name' && (
                    sortDirection === 'asc' ?
                      <RiArrowDownSLine className={getMarginClass("start", 1)} /> :
                      <RiArrowUpSLine className={getMarginClass("start", 1)} />
                  )}
                </div>
              </th>
              <th
                scope="col"
                className={`px-3 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                  } uppercase tracking-wider cursor-pointer`}
                onClick={() => handleSort('apartmentNumber')}
              >
                <div className="flex items-center">
                  {t('apartment')}
                  {sortField === 'apartmentNumber' && (
                    sortDirection === 'asc' ?
                      <RiArrowDownSLine className={getMarginClass("start", 1)} /> :
                      <RiArrowUpSLine className={getMarginClass("start", 1)} />
                  )}
                </div>
              </th>
              <th
                scope="col"
                className={`px-3 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                  } uppercase tracking-wider cursor-pointer`}
                onClick={() => handleSort('rent')}
              >
                <div className="flex items-center">
                  {t('rent')}
                  {sortField === 'rent' && (
                    sortDirection === 'asc' ?
                      <RiArrowDownSLine className={getMarginClass("start", 1)} /> :
                      <RiArrowUpSLine className={getMarginClass("start", 1)} />
                  )}
                </div>
              </th>
              <th
                scope="col"
                className={`px-3 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                  } uppercase tracking-wider cursor-pointer`}
                onClick={() => handleSort('contractEndDate')}
              >
                <div className="flex items-center">
                  {t('contract')}
                  {sortField === 'contractEndDate' && (
                    sortDirection === 'asc' ?
                      <RiArrowDownSLine className={getMarginClass("start", 1)} /> :
                      <RiArrowUpSLine className={getMarginClass("start", 1)} />
                  )}
                </div>
              </th>
              <th
                scope="col"
                className={`px-3 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                  } uppercase tracking-wider cursor-pointer`}
                onClick={() => handleSort('rentStatus')}
              >
                <div className="flex items-center">
                  {t('status')}
                  {sortField === 'rentStatus' && (
                    sortDirection === 'asc' ?
                      <RiArrowDownSLine className={getMarginClass("start", 1)} /> :
                      <RiArrowUpSLine className={getMarginClass("start", 1)} />
                  )}
                </div>
              </th>
              <th
                scope="col"
                className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                  } uppercase tracking-wider`}
              >
                {t('actions')}
              </th>
            </tr>
          </thead>
          <tbody className={`${isDarkMode ? 'bg-zinc-900' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {sortedTenants.map((tenant) => {
              const isExpanded = expandedTenants[tenant.id] || false;
              const owner = tenant.ownerId ? ownerDetails[tenant.ownerId] : null;
              const isLoading = loadingOwners[tenant.id] || false;

              return (
                <React.Fragment key={tenant.id}>
                  <tr className={`${isDarkMode
                    ? 'hover:bg-gray-750 transition-colors'
                    : 'hover:bg-gray-50 transition-colors'
                    } ${isExpanded ? (isDarkMode ? 'bg-gray-750' : 'bg-gray-50') : ''}`}>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedTenants.includes(tenant.id)}
                        onChange={() => handleSelectTenant(tenant.id)}
                        className={`h-4 w-4 rounded ${isDarkMode ? 'bg-zinc-800 border-gray-600' : 'bg-white border-gray-300'
                          } text-red-600 focus:ring-red-500`}
                      />
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {tenant.name || formatTenantDisplayName(tenant)}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleTenantExpansion(tenant.id, tenant.ownerId)}
                        className={`flex items-center gap-1 focus:outline-none ${isDarkMode ? 'hover:text-white' : 'hover:text-gray-800'
                          }`}
                      >
                        <div>
                          <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {tenant.apartmentNumber || t('not_assigned')}
                            {tenant.floor && ` · ${t('floor')} ${tenant.floor}`}
                          </div>
                          <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                            {tenant.buildingName || ''}
                          </div>
                          <div className={`text-xs mt-1 flex items-center ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                            <RiPhoneLine className={getMarginClass("end", 1)} size={12} />
                            {tenant.phone}
                          </div>
                        </div>
                        {tenant.ownerId && (
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDarkMode
                            ? 'bg-zinc-800 text-zinc-400'
                            : 'bg-gray-200 text-gray-500'
                            }`}>
                            {isExpanded ? (
                              <RiArrowUpSLine size={14} />
                            ) : (
                              <RiArrowDownSLine size={14} />
                            )}
                          </div>
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(tenant.rent || 0)}
                      </div>
                      {tenant.discount > 0 && (
                        <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                          {t('after_discount')}: <span className="font-medium">{formatCurrency(tenant.rentAfterDiscount)}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatDate(tenant.contractStartDate)}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                        {t('to')}: <span className="font-medium">{formatDate(tenant.contractEndDate)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {isLeaseExpired(tenant) && tenant.rentStatus !== 'expired' && (
                          <span className={`inline-flex w-fit px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-600'}`}>
                            {t('lease_ended')}
                          </span>
                        )}

                        {/* Show current vs future status if applicable */}
                        {tenant.status === 'active' && (
                          <span className={`inline-flex px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full w-fit ${getRentStatusColor(tenant.rentStatus, isDarkMode)} shadow-sm`}>
                            {tenant.rentStatus ? t(tenant.rentStatus.toLowerCase()) : t('unknown')}
                          </span>
                        )}

                        {tenant.nextBillingDate && (
                          <div className={`text-[11px] mt-1 flex items-center ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                            <RiCalendarCheckLine className="me-1" size={12} />
                            {t('paid_until')}: <span className="font-bold ms-1">{formatDate(tenant.nextBillingDate)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <div className={`flex items-center justify-center ${isRTL ? 'space-x-reverse' : ''} gap-2`}>
                        <Link
                          to={`/admin/tenants/${tenant.id}`}
                          className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-red-900 bg-opacity-40 text-red-400 hover:bg-opacity-60' : 'bg-red-50 text-red-600 hover:bg-red-100'
                            }`}
                        >
                          <RiEyeLine className={getMarginClass("end", 1)} size={16} />
                          {t('view_details')}
                        </Link>

                        <Link
                          to={`/admin/tenants/${tenant.id}/edit`}
                          className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          title={t('edit_tenant')}
                        >
                          <RiPencilLine size={16} />
                        </Link>


                        {(getCurrentUser()?.role === 'admin' || getCurrentUser()?.role === 'front_desk' || getCurrentUser()?.role === 'sales') && (
                          <button
                            onClick={() => openDeleteDialog(tenant.id, tenant.name || formatTenantDisplayName(tenant))}
                            className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'bg-zinc-800 text-red-400 hover:bg-gray-600' : 'bg-gray-100 text-red-600 hover:bg-red-100'
                              }`}
                            title={t('delete_tenant')}
                          >
                            <RiDeleteBin6Line size={16} />
                          </button>
                        )}
                      </div >
                    </td >
                  </tr >

                  {/* Expanded Apartment Owner and Details Row */}
                  {
                    isExpanded && (
                      <tr className={isDarkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-200'}>
                        <td colSpan="7" className="px-4 py-4">
                          <div className={getPaddingClass("start", 12)}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-start">
                              {/* Owner Information */}
                              <div>
                                <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center`}>
                                  <RiUser3Line className={getMarginClass("end", 2)} /> {t('apartment_owner')}
                                </h4>

                                {isLoading ? (
                                  <div className="flex items-center py-2">
                                    <div className={`inline-block h-5 w-5 animate-spin rounded-full border-2 ${isDarkMode ? 'border-red-400 border-t-transparent' : 'border-red-500 border-t-transparent'
                                      }`}></div>
                                    <span className={`${getMarginClass("start", 2)} text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                      {t('loading_owner_details')}
                                    </span>
                                  </div>
                                ) : !owner ? (
                                  <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'} p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                    <div className="flex items-center justify-center py-3">
                                      <RiInformationLine size={20} className={getMarginClass("end", 2)} />
                                      {t('no_owner_information')}
                                    </div>
                                  </div>
                                ) : (
                                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                    <div className="flex items-start">
                                      <div className={`h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md ${getMarginClass("end", 3)} flex-shrink-0`}>
                                        <span className="font-bold text-white text-sm">
                                          {owner.name?.charAt(0)}
                                        </span>
                                      </div>
                                      <div className="flex-1">
                                        <h5 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{owner.name}</h5>
                                        <div className="flex items-center text-sm mt-1">
                                          <RiPhoneLine className={`${getMarginClass("end", 2)} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={14} />
                                          <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{owner.phone}</span>
                                        </div>
                                      </div>
                                      <Link
                                        to={`/admin/owners/${owner.id}`}
                                        className={`flex-shrink-0 inline-flex items-center px-3 py-1 text-xs rounded-full ${isDarkMode ? 'bg-red-900 bg-opacity-40 text-red-400 hover:bg-opacity-60' : 'bg-zinc-50 text-red-600 hover:bg-zinc-100'}`}
                                      >
                                        <RiUser6Line className={getMarginClass("end", 1)} size={12} />
                                        {t('view_profile')}
                                      </Link>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 mt-4">
                                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                                        <p className={`text-xs uppercase ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'} mb-1`}>{t('lives_on_property')}</p>
                                        <div className="flex items-center">
                                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${owner.livingThere
                                            ? (isDarkMode ? 'bg-green-900 bg-opacity-40 text-green-400' : 'bg-green-100 text-green-600')
                                            : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-200 text-gray-500')
                                            }`}>
                                            {owner.livingThere ? <RiCheckLine size={14} /> : <RiCloseCircleLine size={14} />}
                                          </div>
                                          <p className={`${getMarginClass("start", 2)} font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            {owner.livingThere ? t('yes') : t('no')}
                                          </p>
                                        </div>
                                      </div>
                                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                                        <p className={`text-xs uppercase ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'} mb-1`}>{t('total_apartments')}</p>
                                        <div className="flex items-center">
                                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900 bg-opacity-40 text-red-400' : 'bg-zinc-100 text-red-600'}`}>
                                            <RiHomeLine size={14} />
                                          </div>
                                          <p className={`${getMarginClass("start", 2)} font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            {owner.total_apartments || '1'}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Apartment & Financial Details */}
                              <div>
                                <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center`}>
                                  <RiHomeGearLine className={getMarginClass("end", 2)} /> {t('apartment_financial_details')}
                                </h4>

                                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                                      <div className="flex items-center mb-1">
                                        <RiRulerLine className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'} size={14} />
                                        <p className={`${getMarginClass("start", 1)} text-xs uppercase ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('floor')}</p>
                                      </div>
                                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {tenant.floor || t('unknown')}
                                      </p>
                                    </div>
                                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                                      <div className="flex items-center mb-1">
                                        <RiHomeSmileLine className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'} size={14} />
                                        <p className={`${getMarginClass("start", 1)} text-xs uppercase ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('furnished')}</p>
                                      </div>
                                      <div className="flex items-center">
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${tenant.isFurnished
                                          ? (isDarkMode ? 'bg-green-900 bg-opacity-40 text-green-400' : 'bg-green-100 text-green-600')
                                          : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-200 text-gray-500')
                                          }`}>
                                          {tenant.isFurnished ? <RiCheckLine size={12} /> : <RiCloseCircleLine size={12} />}
                                        </div>
                                        <p className={`${getMarginClass("start", 1)} text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                          {tenant.isFurnished ? t('yes') : t('no')}
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                  <h5 className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'} flex items-center`}>
                                    <RiWallet3Line className={getMarginClass("end", 1)} size={14} />
                                    {t('financial_details')}
                                  </h5>

                                  <div className="grid grid-cols-3 gap-2">
                                    <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'} text-center`}>
                                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('service_fee')}</p>
                                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                                        {formatCurrency(tenant.serviceFee || 0)}
                                      </p>
                                    </div>
                                    <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'} text-center`}>
                                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('commission')}</p>
                                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                                        {formatCurrency(tenant.officeCommission || 0)}
                                      </p>
                                    </div>
                                    <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'} text-center`}>
                                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('insurance')}</p>
                                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                                        {formatCurrency(tenant.insurance || 0)}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-3 mt-3">
                                    <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('payment_date')}</p>
                                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                                        {formatDate(tenant.paymentDate) || t('not_set')}
                                      </p>
                                    </div>
                                    <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-650' : 'bg-gray-50'}`}>
                                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('paid_until')}</p>
                                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                                        {formatDate(tenant.nextBillingDate) || t('not_set')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Main Component Render
  return (
    <div className={`${isRTL ? 'rtl' : 'ltr'}`}>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg max-w-md w-full p-6 ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="flex items-center mb-4 text-red-500">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900 bg-opacity-30' : 'bg-zinc-100'} ${getMarginClass("end", 3)}`}>
                <RiAlertLine size={24} />
              </div>
              <h3 className="text-xl font-semibold">{t('confirm_deletion')}</h3>
            </div>

            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-6 px-2`}>
              {t('delete_tenant_confirmation', { name: deleteDialog.tenantName })}
            </p>

            <div className={`flex justify-end ${isRTL ? 'space-x-reverse' : ''} gap-3`}>
              <button
                onClick={closeDeleteDialog}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors duration-200`}
                disabled={deleting}
              >
                {t('cancel')}
              </button>

              <button
                onClick={handleDeleteTenant}
                className={`px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center transition-colors duration-200 ${deleting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin me-2"></div>
                    {t('deleting')}
                  </>
                ) : (
                  <>
                    <RiDeleteBin6Line className={getMarginClass("end", 2)} />
                    {t('delete')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="space-y-6">
        {/* Header & Filters Panel */}
        <div className={`rounded-xl ${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} shadow-md border overflow-hidden`}>
          {/* Header */}
          <div className="p-5 text-start">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} gap-4`}>
                <div className={`h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md`}>
                  <RiUserLine className="text-white" size={20} />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('tenants_management')}
                  </h2>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('manage_tenants_and_leases')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefresh}
                  className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } transition-colors duration-200 flex items-center`}
                  title={t('refresh_tenants')}
                >
                  <RiRefreshLine size={18} />
                  <span className={`${getMarginClass("start", 1)} hidden md:inline`}>{t('refresh')}</span>
                </button>

                <Link
                  to="/admin/tenants/add"
                  className={`px-4 py-2 rounded-lg flex items-center ${isDarkMode
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                    } transition-colors duration-200`}
                >
                  <RiAddLine className={getMarginClass("end", 2)} />
                  {t('add_tenant')}
                </Link>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-green-50'} flex items-center group hover:shadow-md transition-all duration-200`}>
                <div className={`p-3 rounded-full ${getMarginClass("end", 3)} ${isDarkMode ? 'bg-green-600 bg-opacity-20 group-hover:bg-opacity-30' : 'bg-green-100 group-hover:bg-green-200'} transition-colors duration-200`}>
                  <RiCheckLine className={`text-xl ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                </div>
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('active_tenants')}
                  </p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {activeTenantsCount}
                  </p>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'} flex items-center group hover:shadow-md transition-all duration-200`}>
                <div className={`p-3 rounded-full ${getMarginClass("end", 3)} ${isDarkMode ? 'bg-red-600 bg-opacity-20 group-hover:bg-opacity-30' : 'bg-zinc-100 group-hover:bg-red-200'} transition-colors duration-200`}>
                  <RiCloseCircleLine className={`text-xl ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
                </div>
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('inactive_tenants')}
                  </p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {inactiveTenantsCount}
                  </p>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'} flex items-center group hover:shadow-md transition-all duration-200`}>
                <div className={`p-3 rounded-full ${getMarginClass("end", 3)} ${isDarkMode ? 'bg-red-600 bg-opacity-20 group-hover:bg-opacity-30' : 'bg-zinc-100 group-hover:bg-red-200'} transition-colors duration-200`}>
                  <RiHomeSmileLine className={`text-xl ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
                </div>
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('with_apartments')}
                  </p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {tenantsWithActiveLeasesCount}
                  </p>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-yellow-50'} flex items-center group hover:shadow-md transition-all duration-200`}>
                <div className={`p-3 rounded-full ${getMarginClass("end", 3)} ${isDarkMode ? 'bg-yellow-600 bg-opacity-20 group-hover:bg-opacity-30' : 'bg-yellow-100 group-hover:bg-yellow-200'} transition-colors duration-200`}>
                  <RiUserLine className={`text-xl ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
                </div>
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('without_apartments')}
                  </p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {tenantsWithoutActiveLeasesCount}
                  </p>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mt-6 flex flex-col md:flex-row gap-3">
              <div className="relative flex-grow">
                <div className={`absolute inset-y-0 ${isRTL ? 'end-0 pe-3' : 'start-0 ps-3'} flex items-center pointer-events-none text-zinc-400`}>
                  <RiSearchLine size={18} />
                </div>
                <input
                  type="search"
                  className={`block w-full ${isRTL ? 'pe-10' : 'ps-10'} p-2.5 text-sm ${isDarkMode
                    ? 'bg-zinc-800 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                    } border rounded-lg focus:ring-red-500 focus:border-red-500 transition-colors duration-200`}
                  placeholder={t('search_tenants_placeholder')}
                  value={filters.searchTerm}
                  onChange={handleSearch}
                  onKeyPress={e => e.key === 'Enter' && handleApplySearch()}
                />
              </div>

              <div className={`flex flex-wrap gap-2 ${isRTL ? 'space-x-reverse' : ''} `}>
                <button
                  onClick={() => setFilterOpen(!filterOpen)}
                  className={`px-3 py-2.5 text-sm rounded-lg flex items-center ${filterOpen
                    ? (isDarkMode
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-red-600 text-white hover:bg-red-700')
                    : (isDarkMode
                      ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600 border-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300')
                    } border transition-colors duration-200`}
                >
                  <RiFilter2Line className={getMarginClass("end", 2)} />
                  {t('filters')}
                  {Object.values(filters).some(val => val !== '' && val !== filters.searchTerm && val !== filters.showInactive) && (
                    <span className={`${getMarginClass("start", 2)} h-5 w-5 flex items-center justify-center rounded-full text-xs ${isDarkMode ? 'bg-white text-red-600' : 'bg-white text-red-600'
                      }`}>
                      {Object.values(filters).filter(val => val !== '' && val !== filters.searchTerm && val !== filters.showInactive).length}
                    </span>
                  )}
                </button>

                {/* View Mode Toggle */}
                <button
                  onClick={toggleViewMode}
                  className={`px-3 py-2.5 text-sm rounded-lg flex items-center ${isDarkMode
                    ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600 border-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                    } border transition-colors duration-200`}
                  title={viewMode === 'grid' ? t('switch_to_list_view') : t('switch_to_grid_view')}
                >
                  {viewMode === 'grid' ? (
                    <>
                      <RiListUnordered className={getMarginClass("end", 2)} />
                      {t('list_view')}
                    </>
                  ) : (
                    <>
                      <RiLayoutGridLine className={getMarginClass("end", 2)} />
                      {t('grid_view')}
                    </>
                  )}
                </button>

                <button
                  onClick={toggleShowInactive}
                  className={`px-3 py-2.5 text-sm rounded-lg flex items-center ${!filters.showInactive
                    ? (isDarkMode
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-red-600 text-white hover:bg-red-700')
                    : (isDarkMode
                      ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600 border-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300')
                    } ${filters.showInactive ? 'border' : ''} transition-colors duration-200`}
                  title={filters.showInactive ? t('hide_inactive_tenants') : t('show_inactive_tenants')}
                >
                  {filters.showInactive ? (
                    <>
                      <RiEyeOffLine className={getMarginClass("end", 2)} />
                      {t('hide_inactive')}
                    </>
                  ) : (
                    <>
                      <RiEyeLine className={getMarginClass("end", 2)} />
                      {t('show_inactive')}
                    </>
                  )}
                </button>

                {/* Bulk Actions Menu */}
                {selectedTenants.length > 0 && (
                  <div className={`px-3 py-2.5 rounded-lg ${isDarkMode ? 'bg-red-600' : 'bg-red-600'} text-white flex items-center transition-all duration-200 shadow-md`}>
                    <span className={`${getMarginClass("end", 2)} text-sm flex items-center`}>
                      <RiFileUserLine className={getMarginClass("end", 1)} />
                      {selectedTenants.length} {t('selected')}
                    </span>
                    <button
                      onClick={handleBulkDelete}
                      className={`px-2 py-1 rounded ${isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'
                        } text-white ${getMarginClass("start", 2)} text-xs transition-colors duration-200`}
                      title={t('delete_selected_tenants')}
                    >
                      <RiDeleteBin6Line className={`${getMarginClass("end", 1)} inline-block`} size={14} />
                      {t('delete')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter dropdown panel */}
          {filterOpen && (
            <div className={`px-5 py-4 ${isDarkMode ? 'bg-gray-750 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('rent_status')}
                  </label>
                  <select
                    value={filters.rentStatus}
                    onChange={(e) => setFilters({ ...filters, rentStatus: e.target.value })}
                    className={`w-full p-2.5 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-800 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      } border focus:ring-red-500 focus:border-red-500 transition-colors duration-200`}
                  >
                    <option value="">{t('all_statuses')}</option>
                    {TENANT_RENT_STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {t(option.value.toLowerCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('building')}
                  </label>
                  <select
                    value={filters.buildingId}
                    onChange={(e) => setFilters({ ...filters, buildingId: e.target.value })}
                    className={`w-full p-2.5 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-800 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      } border focus:ring-red-500 focus:border-red-500 transition-colors duration-200`}
                  >
                    <option value="">{t('all_buildings')}</option>
                    {/* Building options would be dynamically loaded here */}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('apartment')}
                  </label>
                  <select
                    value={filters.apartmentId}
                    onChange={(e) => setFilters({ ...filters, apartmentId: e.target.value })}
                    className={`w-full p-2.5 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-800 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      } border focus:ring-red-500 focus:border-red-500 transition-colors duration-200`}
                  >
                    <option value="">{t('all_apartments')}</option>
                    {/* Apartment options would be dynamically loaded here */}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('min_rent')}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'end-0 pe-3' : 'start-0 ps-3'} flex items-center pointer-events-none ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      <RiMoneyDollarCircleLine size={16} />
                    </div>
                    <input
                      type="number"
                      value={filters.minRent}
                      onChange={(e) => setFilters({ ...filters, minRent: e.target.value })}
                      className={`w-full ${isRTL ? 'pe-10' : 'ps-10'} p-2.5 text-sm rounded-lg ${isDarkMode
                        ? 'bg-zinc-800 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        } border focus:ring-red-500 focus:border-red-500 transition-colors duration-200`}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('max_rent')}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'end-0 pe-3' : 'start-0 ps-3'} flex items-center pointer-events-none ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      <RiMoneyDollarCircleLine size={16} />
                    </div>
                    <input
                      type="number"
                      value={filters.maxRent}
                      onChange={(e) => setFilters({ ...filters, maxRent: e.target.value })}
                      className={`w-full ${isRTL ? 'pe-10' : 'ps-10'} p-2.5 text-sm rounded-lg ${isDarkMode
                        ? 'bg-zinc-800 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        } border focus:ring-red-500 focus:border-red-500 transition-colors duration-200`}
                      placeholder={t('unlimited')}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('contracts_ending_before')}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${isRTL ? 'end-0 pe-3' : 'start-0 ps-3'} flex items-center pointer-events-none ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      <RiCalendarLine size={16} />
                    </div>
                    <input
                      type="date"
                      value={filters.contractEnd}
                      onChange={(e) => setFilters({ ...filters, contractEnd: e.target.value })}
                      className={`w-full ${isRTL ? 'pe-10' : 'ps-10'} p-2.5 text-sm rounded-lg ${isDarkMode
                        ? 'bg-zinc-800 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        } border focus:ring-red-500 focus:border-red-500 transition-colors duration-200`}
                    />
                  </div>
                </div>
              </div>

              <div className={`flex justify-end mt-4 ${isRTL ? 'space-x-reverse' : ''} gap-3`}>
                <button
                  onClick={() => {
                    setFilters({
                      ...filters,
                      rentStatus: '',
                      buildingId: '',
                      apartmentId: '',
                      minRent: '',
                      maxRent: '',
                      contractEnd: ''
                    });
                  }}
                  className={`px-4 py-2 text-sm rounded-lg ${isDarkMode
                    ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    } transition-colors duration-200`}
                >
                  <RiFilterLine className={`${getMarginClass("end", 1)} inline-block`} size={14} />
                  {t('clear_filters')}
                </button>
                <button
                  onClick={() => setFilterOpen(false)}
                  className={`px-4 py-2 text-sm rounded-lg ${isDarkMode
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                    } transition-colors duration-200`}
                >
                  <RiCheckLine className={`${getMarginClass("end", 1)} inline-block`} size={14} />
                  {t('apply_filters')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} shadow-md border`}>
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className={`inline-block h-12 w-12 animate-spin rounded-full border-4 ${isDarkMode ? 'border-red-500 border-t-transparent' : 'border-red-600 border-t-transparent'
                }`}></div>
              <p className={`mt-6 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('loading_tenants')}
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="p-8">
              <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-50 text-red-700 border border-red-200'
                } flex flex-col items-center justify-center text-center`}>
                <div className={`h-16 w-16 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900 bg-opacity-30' : 'bg-zinc-100'} mb-4`}>
                  <RiAlertLine className="text-4xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('error_loading_tenants')}</h3>
                <p className="mb-6 max-w-md">{error}</p>
                <button
                  onClick={handleRefresh}
                  className={`px-5 py-2.5 rounded-lg ${isDarkMode ? 'bg-red-700 text-white hover:bg-red-600' : 'bg-red-600 text-white hover:bg-red-700'
                    } flex items-center transition-colors duration-200`}
                >
                  <RiRefreshLine className={getMarginClass("end", 2)} />
                  {t('try_again')}
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && tenants.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className={`p-8 rounded-full ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-100'} mb-6`}>
                <RiUserLine className={`text-6xl ${isDarkMode ? 'text-gray-500' : 'text-zinc-400'}`} />
              </div>
              <h3 className={`text-xl font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('no_tenants_found')}
              </h3>
              <p className={`text-sm mb-6 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'} max-w-md text-center`}>
                {t('no_tenants_message')}
              </p>
              <Link
                to="/admin/tenants/add"
                className={`px-5 py-2.5 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'
                  } flex items-center transition-colors duration-200`}
              >
                <RiAddLine className={getMarginClass("end", 2)} />
                {t('create_first_tenant')}
              </Link>
            </div>
          )}

          {/* Tenants Display - Grid or List View */}
          {!loading && !error && filteredTenants.length > 0 && (
            <div className="p-5 text-start">
              {viewMode === 'grid' ? <GridView /> : <ListView />}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} gap-4`}>
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} gap-3`}>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                      className={`p-2 text-sm rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
                        } transition-colors duration-200`}
                    >
                      <option value="10">10 / {t('page')}</option>
                      <option value="25">25 / {t('page')}</option>
                      <option value="50">50 / {t('page')}</option>
                      <option value="100">100 / {t('page')}</option>
                    </select>
                    <span className="text-sm">
                      {t('showing_items', { start: startItem, end: endItem, total: totalItems })}
                    </span>
                  </div>

                  <div className="flex items-center justify-center">
                    <nav className={`inline-flex rounded-md shadow-sm -space-x-px ${isRTL ? 'flex-row-reverse' : ''}`} aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-${isRTL ? 'r' : 'l'}-lg ${isDarkMode
                          ? 'bg-zinc-800 border-gray-600 text-gray-300 hover:bg-gray-600 disabled:text-gray-500 disabled:bg-zinc-900 disabled:hover:bg-zinc-900'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 disabled:text-gray-300 disabled:bg-gray-100 disabled:hover:bg-gray-100'
                          } border text-sm font-medium transition-colors duration-200`}
                      >
                        <span className="sr-only">{t('first_page')}</span>
                        {isRTL ? (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M4.293 15.707a1 1 0 01-1.414-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L10 9.414l-5.293 5.293a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 111.414 1.414L9.414 10l5.293 5.293a1 1 0 010 1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 ${isDarkMode
                          ? 'bg-zinc-800 border-gray-600 text-gray-300 hover:bg-gray-600 disabled:text-gray-500 disabled:bg-zinc-900 disabled:hover:bg-zinc-900'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 disabled:text-gray-300 disabled:bg-gray-100 disabled:hover:bg-gray-100'
                          } border text-sm font-medium transition-colors duration-200`}
                      >
                        <span className="sr-only">{t('previous_page')}</span>
                        {isRTL ? (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>

                      {/* Page Numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        // Show pages around current page
                        let pageNum;
                        if (totalPages <= 5) {
                          // Less than 5 pages, show all
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          // Near start
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          // Near end
                          pageNum = totalPages - 4 + i;
                        } else {
                          // Middle
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                              ? isDarkMode
                                ? 'z-10 bg-red-600 border-red-700 text-white'
                                : 'z-10 bg-red-600 border-red-600 text-white'
                              : isDarkMode
                                ? 'bg-zinc-800 border-gray-600 text-gray-300 hover:bg-gray-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              } transition-colors duration-200`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 ${isDarkMode
                          ? 'bg-zinc-800 border-gray-600 text-gray-300 hover:bg-gray-600 disabled:text-gray-500 disabled:bg-zinc-900 disabled:hover:bg-zinc-900'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 disabled:text-gray-300 disabled:bg-gray-100 disabled:hover:bg-gray-100'
                          } border text-sm font-medium transition-colors duration-200`}
                      >
                        <span className="sr-only">{t('next_page')}</span>
                        {isRTL ? (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-${isRTL ? 'l' : 'r'}-lg ${isDarkMode
                          ? 'bg-zinc-800 border-gray-600 text-gray-300 hover:bg-gray-600 disabled:text-gray-500 disabled:bg-zinc-900 disabled:hover:bg-zinc-900'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 disabled:text-gray-300 disabled:bg-gray-100 disabled:hover:bg-gray-100'
                          } border text-sm font-medium transition-colors duration-200`}
                      >
                        <span className="sr-only">{t('last_page')}</span>
                        {isRTL ? (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M15.707 4.293a1 1 0 010 1.414L9.414 12l6.293 6.293a1 1 0 01-1.414 1.414l-7-7a1 1 0 010-1.414l7-7a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0l7 7a1 1 0 010 1.414l-7 7a1 1 0 01-1.414-1.414L10.586 12 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </nav>
                  </div>
                </div>
              )}

              {/* Filtered results info */}
              {!loading && !error && tenants.length > 0 && filteredTenants.length !== tenants.length && (
                <div className={`mt-4 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'} text-center`}>
                  {t('showing_filtered_results', {
                    showing: filteredTenants.length,
                    total: tenants.length
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantList;